import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Col,
  Row,
  Button,
  Input,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledTooltip,
} from "reactstrap";

import ToolkitProvider, {
  CSVExport
} from "react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import StatementUnavailbleTooltip from "./statementUnavailableTooltip";
import { sendAuditEvent } from "../utils/auditTrail";
import TenantContext from "../utils/tenantContext";

const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const SUBUSER_ACCOUNTING_TIMING_NOTE_BODY =
  "It may take from 15 minutes to 24 hours for the subuser accounting to be completed.";

const StatementTable = ({ tableData, searchQuery, defaultSorted, auditData, subuserReportTooling }) => {  

  const { ExportCSVButton } = CSVExport;
  const { tenant } = useContext(TenantContext);
  const [queueingStatementId, setQueueingStatementId] = useState(null);
  const [subuserQueueMessage, setSubuserQueueMessage] = useState(null);
  const [subuserQueueError, setSubuserQueueError] = useState(null);
  const [subuserGenerateConfirmRow, setSubuserGenerateConfirmRow] = useState(null);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ];

  const currencies = ["€", "$", "£"];

  const monthFormatter = (cell, row) => {
    cell = months[cell - 1];
    return cell;
  };

  const amountFormatter = (cell, row) => `${cell}${currencies[row.currency]}`;

  const statusFormatter = cell =>
    cell === 0 ? (
      <Button className="btn-status" disabled color="warning">
        Open
      </Button>
    ) : cell === 1 ? (
      <Button className="btn-status" disabled color="success">
        Paid
      </Button>
    ) : cell === 2 ? (
      <Button className="btn-status" disabled color="info">
        On Hold
      </Button>
    ) : null;

  const typeFormatter = cell =>
    cell === 1
      ? (cell = "Licensing")
      : cell === 2
      ? "Regular"
      : cell === 3
      ? "Audio Mastering - Services"
      : cell === 4
      ? "Vinyl Earnings"
      : cell === 5
      ? "Offset"
      : "Not known";

  const downloadFormatter = (cell, row) =>
    cell ?
    (
      <a
        href={cell}
        target="blank"
        rel="noreferrer"
        onClick={() => {
          sendAuditEvent({
            token: auditData?.token,
            eventType: 3,
            route: auditData?.route || window.location.pathname,
            entityType: "statement",
            entityIds: [row.id],
            tenantContext: tenant,
          });
        }}
      >
        <Button className="btn-status" color="primary">
          <b>Download</b>
        </Button>
      </a>
    ) :
    <center>
      <StatementUnavailbleTooltip id={row.id} />
    </center>;

  const queueSubuserReport = row => {
    if (!subuserReportTooling?.token) return;
    setSubuserQueueError(null);
    setSubuserQueueMessage(null);
    setQueueingStatementId(row.id);
    axios
      .post(
        `${API_URL}statements/${row.id}/queue-subuser-report/`,
        {},
        {
          headers: {
            Authorization: `Bearer ${subuserReportTooling.token}`,
            "x-api-key": X_API_KEY,
            "Content-Type": "application/json",
          },
        },
      )
      .then(res => {
        const detail = res.data?.detail || "Request completed.";
        setSubuserQueueMessage(`${detail}`);
        if (subuserReportTooling.onQueued) {
          subuserReportTooling.onQueued();
        }
      })
      .catch(err => {
        const data = err.response?.data;
        const msg =
          (typeof data === "object" && data?.detail) ||
          (typeof data === "string" ? data : null) ||
          err.message ||
          "Could not queue subuser report generation.";
        setSubuserQueueError(msg);
      })
      .finally(() => setQueueingStatementId(null));
  };

  const confirmGenerateSubuserReport = () => {
    if (!subuserGenerateConfirmRow) return;
    const row = subuserGenerateConfirmRow;
    setSubuserGenerateConfirmRow(null);
    queueSubuserReport(row);
  };

  const subuserReportBtnStyle = { width: "auto", maxWidth: "100%" };

  const subuserReportColumn = {
    dataField: "subuser_accounting_status",
    text: "Subuser Acc",
    sort: false,
    csvExport: false,
    headerAlign: "center",
    align: "center",
    headerStyle: () => ({ width: "112px", maxWidth: "112px" }),
    style: () => ({ width: "112px", maxWidth: "112px" }),
    formatter: (cell, row) => {
      if (!row.user_report_item_id) {
        return <span className="text-muted">—</span>;
      }
      if (cell === "pending") {
        return (
          <Button
          className="btn-status"
          color="primary"
          size="sm"
          style={subuserReportBtnStyle}
          onClick={() => setSubuserGenerateConfirmRow(row)}
        >
          Generate
        </Button>
        );
      }
      if (cell === "in_progress") {
        const generatingTooltipId = `subuser-acc-generating-${row.id}`;
        return (
          <>
            <span id={generatingTooltipId}>
              <Button
                className="btn-status"
                disabled
                color="info"
                size="sm"
                style={{ ...subuserReportBtnStyle, pointerEvents: "none" }}
              >
                {queueingStatementId === row.id ? "…" : "Generating"}
              </Button>
            </span>
            <UncontrolledTooltip placement="top" trigger="hover" target={generatingTooltipId}>
              <strong>Note:</strong> {SUBUSER_ACCOUNTING_TIMING_NOTE_BODY}
            </UncontrolledTooltip>
          </>
        );
      }
      if (cell === "error") {
        return (
          <Button className="btn-status" disabled color="danger" size="sm" style={subuserReportBtnStyle}>
            {queueingStatementId === row.id ? "…" : "Error"}
          </Button>
        );
      }
      if (cell === "completed") {
        const subuserStatementsTo = `/subuser-statements?subuser_report__parent_report=${encodeURIComponent(
          row.user_report_item_id,
        )}`;
        return (
          <Link
            to={subuserStatementsTo}
            className="btn btn-outline-info"
            style={subuserReportBtnStyle}
          >
            View
          </Link>
        );
      }
    },
  };

  const columns = [
    {
      dataField: "sts_kind",
      text: "Type",
      sort: true,
      formatter: typeFormatter,
      csvFormatter: typeFormatter,
      headerStyle: (colum, colIndex) => {
        return { width: "80px" };
      }
    },
    {
      dataField: "year",
      text: "Year",
      sort: true,
      headerStyle: (colum, colIndex) => {
        return { width: "80px" };
      }
    },
    {
      dataField: "month",
      text: "Month",
      sort: true,
      formatter: monthFormatter,
      csvFormatter: cell => months[cell - 1],
      headerStyle: (colum, colIndex) => {
        return { width: "80px" };
      }
    },
    {
      dataField: "issue_year",
      text: "Is. Year",
      sort: true,
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      }
    },
    {
      dataField: "issue_month",
      text: "Is. Month",
      sort: true,
      formatter: monthFormatter,
      csvFormatter: cell => months[cell - 1],
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      }
    },
    {
      dataField: "short_description",
      text: "Description",
      headerStyle: (colum, colIndex) => {
        return { width: "150px" };
      }
    },
    {
      dataField: "price",
      text: "Amount",
      sort: true,
      formatter: amountFormatter,
      csvFormatter: amountFormatter,
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      }
    },
    {
      dataField: "status",
      text: "Status",
      sort: true,
      formatter: statusFormatter,
      csvFormatter: cell =>
        cell === 0 ? "Open" : cell === 1 ? "  Paid" : null,
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      }
    },
    {
      dataField: "new_details",
      text: "Details",
      headerAlign: "center",
      align: "center",
      formatter: (cell, row) => downloadFormatter(cell, row),
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      }
    },
    {
      dataField: "new_overview",
      text: "Overview",
      headerAlign: "center",
      align: "center",
      formatter: (cell, row) => downloadFormatter(cell, row),
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      }
    },

    ...(subuserReportTooling ? [subuserReportColumn] : []),

    //data for search
    {
      dataField: "status",
      sort: false,
      csvExport: false,
      hidden: true,
      formatter: cell => (cell === 0 ? "Open" : cell === 1 ? "Paid" : null)
    }
  ];

  const handleTableChange = (type, { sortField, sortOrder, data }) => {
    const ascDesc = sortOrder === "desc" ? "-" : "";
    if (sortField === "kind" || sortField === "sts_kind" || sortField === "quartal" || sortField === "month" || sortField === "year" || sortField === "issue_month" || sortField === "issue_year" || sortField === "price") {
      searchQuery.setOrdering({order: ascDesc, name: sortField, sort: sortField});
    }
    if (sortField === "name") {
      searchQuery.setOrdering({order: ascDesc, name: "id", sort: sortField});
    }
  }


  const generateConfirmStatementLabel =
    subuserGenerateConfirmRow &&
    (subuserGenerateConfirmRow.short_description ||
      [
        months[(subuserGenerateConfirmRow.month || 1) - 1],
        subuserGenerateConfirmRow.year,
      ]
        .filter(Boolean)
        .join(" "));

  return (
    <div>
      {subuserReportTooling && subuserGenerateConfirmRow && (
        <Modal
          isOpen
          centered
          toggle={() => setSubuserGenerateConfirmRow(null)}
        >
          <ModalHeader toggle={() => setSubuserGenerateConfirmRow(null)}>
            Generate subuser accounting
          </ModalHeader>
          <ModalBody>
            <p className="mb-2">
              Are you sure you want to do the subuser accounting for this statement
              {generateConfirmStatementLabel ? (
                <>
                  {" "}
                  (<strong>{generateConfirmStatementLabel}</strong>)?

                  <br/><br/>
                  <b>Note:</b> {SUBUSER_ACCOUNTING_TIMING_NOTE_BODY}
                </>
              ) : (
                "?"
              )}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setSubuserGenerateConfirmRow(null)}>
              Cancel
            </Button>
            <Button color="primary" onClick={confirmGenerateSubuserReport}>
              Generate
            </Button>
          </ModalFooter>
        </Modal>
      )}
      <ToolkitProvider
        keyField="id"
        data={tableData}
        columns={columns}
        search={{
          searchFormatted: true
        }}
        exportCSV={ {
          fileName: 'statements.csv',
        } }
      >
        {props => (
          <div>
            {subuserReportTooling && subuserQueueMessage && (
              <Alert color="success" className="mb-2">
                {subuserQueueMessage}
              </Alert>
            )}
            {subuserReportTooling && subuserQueueError && (
              <Alert color="danger" className="mb-2">
                {subuserQueueError}
              </Alert>
            )}
            <Row>
              <Col>
                <div className="customSearch">
                  <Input placeholder="Search..." value={searchQuery.query} style={{marginBottom: 10}} onChange={e => searchQuery.setQuery(e.target.value)}/>
                </div>
              </Col>
              <Col>
                <div className="flexEndContainer">
                                 
                  <ExportCSVButton
                    className="btn btn-outline-info"
                    {...props.csvProps}
                  >
                    Export CSV
                  </ExportCSVButton>
                </div>
              </Col>
            </Row>
            <BootstrapTable
              {...props.baseProps}
              bordered={ false }
              remote={ { sort: true } }
              defaultSorted={defaultSorted}
              onTableChange={handleTableChange}
            />
          </div>
        )}
      </ToolkitProvider>
    </div>
  );
};

export default StatementTable;
